import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, XCircle, Target } from 'lucide-react';
import { ModuleId, Question } from '../../types';
import { useAppStore } from '../../hooks/useAppStore';
import { generatePlacementQuestions, evaluatePlacement, getPlacementDescription } from '../../utils/placementEngine';
import { Button } from '../ui/Button';
import { ConfettiCelebration } from '../ConfettiCelebration';
import { LatexRenderer } from '../ui/LatexRenderer';
interface PlacementTestProps {
  moduleId: ModuleId;
  onComplete: () => void;
  onCancel: () => void;
}
export function PlacementTest({
  moduleId,
  onComplete,
  onCancel
}: PlacementTestProps) {
  const {
    selectedGroup,
    savePlacementResult
  } = useAppStore();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [result, setResult] = useState<ReturnType<typeof evaluatePlacement> | null>(null);
  // Load questions on mount
  useEffect(() => {
    const qs = generatePlacementQuestions(moduleId, selectedGroup);
    setQuestions(qs);
  }, [moduleId, selectedGroup]);
  const handleSelectOption = useCallback((optionIndex: number) => {
    if (showFeedback || selectedOption !== null) return; // Prevent double-click
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return;
    const isCorrect = optionIndex === currentQuestion.correctAnswer;
    setSelectedOption(optionIndex);
    setShowFeedback(true);
    const newAnswers = [...answers, isCorrect];
    setAnswers(newAnswers);
    // Auto-advance after brief feedback
    setTimeout(() => {
      if (newAnswers.length === questions.length) {
        // Test complete
        const correctCount = newAnswers.filter(a => a).length;
        const placementResult = evaluatePlacement(moduleId, selectedGroup, correctCount, questions.length);
        setResult(placementResult);
        savePlacementResult(placementResult);
        setIsFinished(true);
      } else {
        // Next question
        setCurrentIndex(prev => prev + 1);
        setSelectedOption(null);
        setShowFeedback(false);
      }
    }, 800);
  }, [showFeedback, selectedOption, questions, currentIndex, answers, moduleId, selectedGroup, savePlacementResult]);
  // Loading state
  if (questions.length === 0) {
    return <div className="flex items-center justify-center p-12" data-id="element-1316">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" data-id="element-1317" />
      </div>;
  }
  // Result View
  if (isFinished && result) {
    const description = getPlacementDescription(result);
    return <div className="p-6 text-center max-w-md mx-auto" data-id="element-1318">
        <ConfettiCelebration trigger={result.score >= 4} data-id="element-1319" />

        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="mb-8" data-id="element-1320">
          <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl" data-id="element-1321">
            <span className="text-4xl" data-id="element-1322">{description.emoji}</span>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1323">
            {description.title}
          </h2>
          <p className="text-slate-600 mb-6 leading-relaxed" data-id="element-1324">
            {description.description}
          </p>

          <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-100" data-id="element-1325">
            <div className="flex justify-between items-center text-sm mb-2" data-id="element-1326">
              <span className="text-slate-500" data-id="element-1327">Jouw score</span>
              <span className="font-bold text-indigo-600" data-id="element-1328">
                {result.score} / {result.totalQuestions}
              </span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-1329">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${result.score / result.totalQuestions * 100}%`
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} className="h-full bg-indigo-500 rounded-full" data-id="element-1330" />
            </div>
          </div>

          {/* Show answer dots */}
          <div className="flex justify-center gap-2 mb-8" data-id="element-1331">
            {answers.map((correct, i) => <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${correct ? 'bg-emerald-500' : 'bg-red-400'}`} data-id="element-1332">
                {correct ? '✓' : '✗'}
              </div>)}
          </div>

          <Button onClick={onComplete} size="lg" fullWidth variant="primary" data-id="element-1333">
            Start met oefenen <ArrowRight className="w-4 h-4 ml-2" data-id="element-1334" />
          </Button>
        </motion.div>
      </div>;
  }
  // Question View
  const currentQuestion = questions[currentIndex];
  if (!currentQuestion) return null;
  return <div className="max-w-2xl mx-auto" data-id="element-1335">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-1336">
        <div className="flex items-center gap-3" data-id="element-1337">
          <div className="bg-indigo-100 p-2 rounded-lg" data-id="element-1338">
            <Target className="w-5 h-5 text-indigo-600" data-id="element-1339" />
          </div>
          <div data-id="element-1340">
            <h3 className="font-bold text-slate-900" data-id="element-1341">Instaptoets</h3>
            <p className="text-xs text-slate-500" data-id="element-1342">
              We kijken even waar je staat
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onCancel} data-id="element-1343">
          Overslaan
        </Button>
      </div>

      {/* Progress Dots */}
      <div className="flex justify-center gap-3 mb-8" data-id="element-1344">
        {questions.map((_, i) => <div key={i} className={`w-3 h-3 rounded-full transition-all duration-300 ${i < answers.length ? answers[i] ? 'bg-emerald-500 scale-100' : 'bg-red-400 scale-100' : i === currentIndex ? 'bg-indigo-500 scale-125 ring-4 ring-indigo-100' : 'bg-slate-200'}`} data-id="element-1345" />)}
      </div>

      {/* Question */}
      <AnimatePresence mode="wait" data-id="element-1346">
        <motion.div key={currentIndex} initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -30
      }} transition={{
        duration: 0.25
      }} data-id="element-1347">
          {/* Question Text */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm" data-id="element-1348">
            <p className="text-xs font-medium text-slate-400 mb-3" data-id="element-1349">
              Vraag {currentIndex + 1} van {questions.length}
            </p>
            <div className="text-lg font-medium text-slate-900 leading-relaxed" data-id="element-1350">
              <LatexRenderer text={currentQuestion.question} data-id="element-1351" />
            </div>
          </div>

          {/* Options - Click to immediately answer */}
          <div className="space-y-3" data-id="element-1352">
            {currentQuestion.options.map((option, i) => {
            const isSelected = selectedOption === i;
            const isCorrectOption = i === currentQuestion.correctAnswer;
            const showCorrect = showFeedback && isCorrectOption;
            const showWrong = showFeedback && isSelected && !isCorrectOption;
            return <motion.button key={i} onClick={() => handleSelectOption(i)} disabled={showFeedback} whileHover={!showFeedback ? {
              scale: 1.01
            } : {}} whileTap={!showFeedback ? {
              scale: 0.99
            } : {}} className={`
                    w-full text-left p-4 rounded-xl border-2 transition-all duration-200
                    flex items-center gap-3
                    ${showCorrect ? 'border-emerald-500 bg-emerald-50' : showWrong ? 'border-red-400 bg-red-50' : showFeedback ? 'border-slate-100 bg-slate-50 opacity-50' : 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30 cursor-pointer'}
                  `} data-id="element-1353">
                  <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0
                      ${showCorrect ? 'bg-emerald-500 text-white' : showWrong ? 'bg-red-400 text-white' : 'bg-slate-100 text-slate-500'}
                    `} data-id="element-1354">
                    {showCorrect ? <CheckCircle2 className="w-5 h-5" data-id="element-1355" /> : showWrong ? <XCircle className="w-5 h-5" data-id="element-1356" /> : String.fromCharCode(65 + i)}
                  </div>
                  <span className={`font-medium ${showCorrect ? 'text-emerald-900' : showWrong ? 'text-red-900' : 'text-slate-700'}`} data-id="element-1357">
                    <LatexRenderer text={option} data-id="element-1358" />
                  </span>
                </motion.button>;
          })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 text-center text-sm text-slate-400" data-id="element-1359">
        <p data-id="element-1360">💡 Klik op het antwoord dat je denkt dat goed is</p>
      </div>
    </div>;
}